import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ItemService } from '../../../../services/item.service';
import { StockService } from '../../../../services/stock.service';
import { WarehousesService } from '../../../../services/warehouses.service';
import { Item, ItemTransferRequest } from '../../../../models/Item';
import { Stock } from '../../../../models/Stock';
import { Warehouse } from '../../../../models/Warehouse';

@Component({
  selector: 'app-item-transfer-modal',
  templateUrl: './item-transfer-modal.component.html',
  styleUrls: ['./item-transfer-modal.component.css']
})
export class ItemTransferModalComponent implements OnInit {
  @Input() item: Item | null = null;
  @Input() isVisible = false;
  @Output() close = new EventEmitter<void>();
  @Output() success = new EventEmitter<void>();

  form: FormGroup;
  stocks: Stock[] = [];
  warehouses: Warehouse[] = [];
  isLoading = false;
  isSubmitting = false;
  transferType: 'Complete' | 'Partial' | 'Location' = 'Complete';
  validationErrors: string[] = [];

  transferTypes = [
    { value: 'Complete', label: 'Transferência Completa', description: 'Transferir o item inteiro para novo estoque/almoxarifado' },
    { value: 'Partial', label: 'Transferência Parcial', description: 'Transferir apenas uma quantidade específica do item' },
    { value: 'Location', label: 'Transferência de Localização', description: 'Mover o item para nova localização dentro do mesmo estoque' }
  ];

  constructor(
    private fb: FormBuilder,
    private itemService: ItemService,
    private stockService: StockService,
    private warehousesService: WarehousesService
  ) {
    this.form = this.fb.group({
      targetStockId: [null, Validators.required],
      targetWarehouseId: [null],
      transferType: ['Complete', Validators.required],
      quantity: [null],
      newLocation: [''],
      newShelf: [null],
      newRack: [null],
      reason: ['', Validators.maxLength(200)],
      notes: ['', Validators.maxLength(1000)]
    });
  }

  ngOnInit(): void {
    this.loadStocks();
    this.loadWarehouses();
    this.setupFormValidation();
  }

  loadStocks(): void {
    this.stockService.getAll({ page: 1, pageSize: 1000 }).subscribe({
      next: (result) => {
        this.stocks = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar estoques:', error);
      }
    });
  }

  loadWarehouses(): void {
    this.warehousesService.getAll({ page: 1, pageSize: 1000 }).subscribe({
      next: (result) => {
        this.warehouses = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar almoxarifados:', error);
      }
    });
  }

  setupFormValidation(): void {
    // Observar mudanças no tipo de transferência
    this.form.get('transferType')?.valueChanges.subscribe(type => {
      this.transferType = type;
      this.updateFormValidation();
    });
  }

  updateFormValidation(): void {
    const quantityControl = this.form.get('quantity');
    const newLocationControl = this.form.get('newLocation');
    const targetStockControl = this.form.get('targetStockId');

    // Limpar validações anteriores
    quantityControl?.clearValidators();
    newLocationControl?.clearValidators();
    targetStockControl?.clearValidators();

    // Aplicar validações baseadas no tipo
    switch (this.transferType) {
      case 'Partial':
        quantityControl?.setValidators([Validators.required, Validators.min(0.01)]);
        targetStockControl?.setValidators([Validators.required]);
        break;
      case 'Location':
        newLocationControl?.setValidators([Validators.required, Validators.maxLength(200)]);
        // Para transferência de localização, não precisa de estoque de destino
        break;
      case 'Complete':
      default:
        targetStockControl?.setValidators([Validators.required]);
        break;
    }

    // Atualizar validações
    quantityControl?.updateValueAndValidity();
    newLocationControl?.updateValueAndValidity();
    targetStockControl?.updateValueAndValidity();
  }

  setTransferType(type: string): void {
    if (type === 'Complete' || type === 'Partial' || type === 'Location') {
      this.transferType = type;
      this.form.patchValue({ transferType: type });
      this.updateFormValidation();
    }
  }

  getTransferTypeDescription(): string {
    const type = this.transferTypes.find(t => t.value === this.transferType);
    return type ? type.description : '';
  }

  isFormValid(): boolean {
    this.form.markAllAsTouched();
    return this.form.valid;
  }

  validateTransfer(): void {
    if (!this.item || !this.isFormValid()) {
      return;
    }

    this.isLoading = true;
    this.validationErrors = [];

    const transferRequest: ItemTransferRequest = {
      itemId: this.item.id,
      targetStockId: this.form.value.targetStockId,
      targetWarehouseId: this.form.value.targetWarehouseId,
      staffId: 1, // TODO: Get from authentication context
      transferType: this.transferType,
      quantity: this.form.value.quantity,
      newLocation: this.form.value.newLocation,
      newShelf: this.form.value.newShelf,
      newRack: this.form.value.newRack,
      reason: this.form.value.reason,
      notes: this.form.value.notes
    };

    this.itemService.validateTransfer(transferRequest).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.isValid) {
          this.submitTransfer();
        } else {
          this.validationErrors = response.validationErrors || [];
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Erro ao validar transferência:', error);
        this.validationErrors = ['Erro ao validar transferência'];
      }
    });
  }

  submitTransfer(): void {
    if (!this.item || !this.isFormValid()) {
      return;
    }

    this.isSubmitting = true;
    this.validationErrors = [];

    const transferRequest: ItemTransferRequest = {
      itemId: this.item.id,
      targetStockId: this.form.value.targetStockId,
      targetWarehouseId: this.form.value.targetWarehouseId,
      staffId: 1, // TODO: Get from authentication context
      transferType: this.transferType,
      quantity: this.form.value.quantity,
      newLocation: this.form.value.newLocation,
      newShelf: this.form.value.newShelf,
      newRack: this.form.value.newRack,
      reason: this.form.value.reason,
      notes: this.form.value.notes
    };

    this.itemService.transferItem(transferRequest).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        if (response.success) {
          this.success.emit();
          this.closeModal();
        } else {
          this.validationErrors = response.validationErrors || ['Erro na transferência'];
        }
      },
      error: (error) => {
        this.isSubmitting = false;
        console.error('Erro ao transferir item:', error);
        this.validationErrors = ['Erro ao transferir item'];
      }
    });
  }

  closeModal(): void {
    this.form.reset();
    this.transferType = 'Complete';
    this.validationErrors = [];
    this.close.emit();
  }

  getStockName(stockId: number): string {
    const stock = this.stocks.find(s => s.id === stockId);
    return stock ? stock.name : 'N/A';
  }

  getWarehouseName(warehouseId?: number): string {
    if (!warehouseId) return 'N/A';
    const warehouse = this.warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.name : 'N/A';
  }

  getTransferTypeIcon(type: string): string {
    switch (type) {
      case 'Complete':
        return 'fas fa-exchange-alt';
      case 'Partial':
        return 'fas fa-cut';
      case 'Location':
        return 'fas fa-map-marker-alt';
      default:
        return 'fas fa-arrows-alt';
    }
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'disponível':
        return 'status-available';
      case 'em uso':
        return 'status-in-use';
      case 'danificado':
        return 'status-damaged';
      case 'vencido':
        return 'status-expired';
      case 'em manutenção':
        return 'status-maintenance';
      case 'em trânsito':
        return 'status-in-transit';
      default:
        return 'status-default';
    }
  }
}
